import {
  Menu,
  ActionIcon,
  Anchor,
  Checkbox,
  Tooltip,
  Avatar,
} from '@mantine/core'
import dayjs from 'dayjs'
import { Copy } from 'lucide-react'

import { useMutation } from '@redwoodjs/web'

import CompanyFeatureGate from 'src/components/CompanyFeatureGate/CompanyFeatureGate'
import DnTable from 'src/components/Core/DnTable'
import AiAnalyzedIndicator from 'src/components/DataDisplay/AiAnalyzedIndicator/AiAnalyzedIndicator'
import ExpirationIndicator from 'src/components/DataDisplay/ExpirationIndicator/ExpirationIndicator'
import StyledBadge from 'src/components/DataDisplay/StyledBadge/StyledBadge'
import TrackerIconButton from 'src/components/DataDisplay/TrackerIconButton/TrackerIconButton'
import StyledMenu from 'src/components/Overlays/StyledMenu/StyledMenu'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { useCurrentCompanyContext } from 'src/context/CurrentCompanyContext'
import { UPDATE_DOCUMENT } from 'src/graphql/documents.graphql'
import { CREATE_TRACKER_FOR_DOCUMENT } from 'src/graphql/expirationTrackers.graphql'
import {
  DocumentCategory,
  DocumentUserStatus,
} from 'src/graphql/types/documents'
import useVoEDocumentCompletionGate from 'src/hooks/useVoEDocumentCompletionGate/useVoEDocumentCompletionGate'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconEmptyInbox from 'src/icons/IconEmptyInbox'
import IconSparkle from 'src/icons/IconSparkle'
import IconThreeDots from 'src/icons/IconThreeDots'
import { buildDocumentCategoryOptions } from 'src/lib/documentCategory.utils'
import {
  canCreateTrackerForDocument,
  getActiveTrackerId,
  isCategoryTracked,
} from 'src/lib/expirationTracking'
import {
  formatSnakeValue,
  buildFullName,
  formatDocumentCategory,
} from 'src/lib/formatters'
import { isSameUtcDay } from 'src/utils/date/toMonthYearKey'

const colorByStatus = {
  PENDING_REVIEW: 'orange',
  COMPLETED: 'green',
}

const AI_BLUE = '#1C93D7'
const AI_AMBER = '#B45309'
const AI_GRAY = '#9CA3AF'
const AI_RED = '#DC2626'
const AI_HIGH_CONFIDENCE_THRESHOLD = 0.9
// Pending high-confidence predictions render the same blue as accepted, just
// faded — that signals "AI suggests this, you haven't acted yet" without
// borrowing amber's "warning" semantics. Amber stays exclusive to the
// genuinely uncertain (<90%) case so it can't be misread when paired with
// a high percentage in the tooltip.
const PENDING_HIGH_OPACITY = 0.55

const AiExpirationIndicator = ({ documentIntelligence, expirationDate }) => {
  if (!documentIntelligence) return null

  const { status, predictions } = documentIntelligence

  let color: string
  let tooltipLabel: string
  let opacity = 1

  if (status === 'PENDING' || status === 'PROCESSING') {
    color = AI_GRAY
    tooltipLabel = 'Analyzing…'
  } else if (status === 'FAILED') {
    return null
  } else if (status === 'COMPLETED') {
    const expirationPred = predictions.find((p) => p.field === 'DATE')
    if (!expirationPred || !expirationPred.expirationValue) return null

    // If the recruiter explicitly rejected this suggestion, hide the indicator
    if (expirationPred.reviewDecision === 'KEPT_ORIGINAL') return null

    const valueMatchesAI =
      expirationDate != null &&
      isSameUtcDay(expirationPred.expirationValue, expirationDate)

    const hasConflictingValue = expirationDate != null && !valueMatchesAI

    const isAccepted =
      expirationPred.reviewDecision === 'ACCEPTED_AI' ||
      (expirationPred.autoApplied && !hasConflictingValue) ||
      valueMatchesAI

    if (isAccepted) {
      color = AI_BLUE
      tooltipLabel = 'AI predicted expiration'
    } else if (hasConflictingValue) {
      color = AI_AMBER
      tooltipLabel = 'AI suggests different expiration'
    } else if (expirationPred.confidence >= AI_HIGH_CONFIDENCE_THRESHOLD) {
      color = AI_BLUE
      opacity = PENDING_HIGH_OPACITY
      tooltipLabel = 'AI predicted'
    } else {
      color = AI_AMBER
      tooltipLabel = 'AI predicted · low confidence'
    }
  } else {
    return null
  }

  return (
    <Tooltip label={tooltipLabel} withArrow className="text-xs">
      <span
        style={{ color, flexShrink: 0, opacity, lineHeight: 0 }}
        className="inline-flex items-center align-middle"
      >
        <IconSparkle size={11} />
      </span>
    </Tooltip>
  )
}

const AiCategoryIndicator = ({ documentIntelligence, category }) => {
  if (!documentIntelligence) return null

  const { status, predictions } = documentIntelligence

  let color: string
  let tooltipLabel: string
  let opacity = 1

  if (status === 'PENDING' || status === 'PROCESSING') {
    color = AI_GRAY
    tooltipLabel = 'Analyzing…'
  } else if (status === 'FAILED') {
    color = AI_RED
    tooltipLabel = 'Analysis failed'
  } else if (status === 'COMPLETED') {
    const categoryPred = predictions.find((p) => p.field === 'CATEGORY')
    if (!categoryPred) return null

    // Treat as accepted if auto-applied OR if the document's category already
    // matches the suggestion (means it was manually accepted in a prior session).
    // If the recruiter explicitly rejected this suggestion, hide the indicator
    if (categoryPred.reviewDecision === 'KEPT_ORIGINAL') return null

    const valueMatchesAI =
      categoryPred.categoryValue != null &&
      categoryPred.categoryValue === category

    const hasConflictingValue =
      categoryPred.categoryValue != null && !valueMatchesAI

    const isAccepted =
      categoryPred.reviewDecision === 'ACCEPTED_AI' ||
      (categoryPred.autoApplied && !hasConflictingValue) ||
      valueMatchesAI

    if (isAccepted) {
      color = AI_BLUE
      tooltipLabel = 'AI classified'
    } else if (hasConflictingValue) {
      color = AI_AMBER
      tooltipLabel = 'AI suggests different category'
    } else if (categoryPred.confidence >= AI_HIGH_CONFIDENCE_THRESHOLD) {
      color = AI_BLUE
      opacity = PENDING_HIGH_OPACITY
      tooltipLabel = 'AI predicted'
    } else {
      color = AI_AMBER
      tooltipLabel = 'AI predicted · low confidence'
    }
  } else {
    return null
  }

  return (
    <Tooltip label={tooltipLabel} withArrow className="text-xs">
      <span
        style={{ color, flexShrink: 0, opacity, lineHeight: 0 }}
        className="inline-flex items-center align-middle"
      >
        <IconSparkle size={11} />
      </span>
    </Tooltip>
  )
}

// Whether a per-field sparkle (AiCategoryIndicator / AiExpirationIndicator) will
// actually render for this field — mirrors those components' null-returns. Used
// to decide the generic AiAnalyzedIndicator fallback: keying the fallback off
// whether a prediction merely *exists* over-suppresses it, because a per-field
// sparkle renders nothing for a rejected (KEPT_ORIGINAL) suggestion or a DATE
// with no expirationValue — leaving an analyzed row with no AI marker at all.
const willFieldSparkleRender = (
  field: 'CATEGORY' | 'DATE',
  documentIntelligence
) => {
  if (!documentIntelligence) return false
  const { status, predictions } = documentIntelligence
  if (status === 'PENDING' || status === 'PROCESSING') return true
  // FAILED: the category indicator shows a red sparkle; the expiration one hides.
  if (status === 'FAILED') return field === 'CATEGORY'
  if (status !== 'COMPLETED') return false
  const pred = predictions?.find((p) => p.field === field)
  if (!pred) return false
  if (field === 'DATE' && !pred.expirationValue) return false
  if (pred.reviewDecision === 'KEPT_ORIGINAL') return false
  return true
}

const categories = buildDocumentCategoryOptions(formatDocumentCategory)

const statuses = Object.values(DocumentUserStatus)

const MAX_DOCUMENTS = 100

const DocumentsTable = ({
  documents = [],
  loading,
  onPreview,
  onEdit,
  onDownload,
  onDelete,
  onRenewed,
  onOpenTracker,
  refetchQueries,
  selectedDocuments = new Set(),
  onSelectDocument,
  onSelectAll,
  applicantInfo,
}) => {
  const currentCompany = useCurrentCompanyContext()
  const showAiIndicators = currentCompany?.documentIntelligenceEnabled ?? false

  const [updateDocument] = useMutation(UPDATE_DOCUMENT, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      toast('Document updated successfully', 'success')
    },
    onError: () => {
      toast('Unable to update document, please try again.', 'error')
    },
  })

  const { requestStatusChange, modal: voeCompletionModal } =
    useVoEDocumentCompletionGate()

  const handleStatusChange = (document, status) => {
    if (status === document.userStatus) return
    requestStatusChange({
      voe: document.voe,
      userStatus: status,
      currentStatus: document.userStatus,
      send: (voeCompletion) =>
        updateDocument({
          variables: {
            id: document.documentId,
            input: {
              userStatus: status,
              ...(voeCompletion && { voeCompletion }),
            },
          },
        }),
    })
  }

  const [createTrackerForDocument] = useMutation(CREATE_TRACKER_FOR_DOCUMENT, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      toast('Expiration tracker created', 'success')
    },
    onError: (error) => {
      toast(error.message || 'Unable to create tracker', 'error')
    },
  })

  const canCreateTrackerFor = (document) =>
    canCreateTrackerForDocument({
      document,
      applicant: applicantInfo,
      expirationTrackingEnabled: currentCompany?.expirationTrackingEnabled,
    })

  // Column definitions for DnTable
  const columns = [
    // Conditional checkbox column
    ...(onSelectDocument
      ? [
          {
            key: 'checkbox',
            label: (
              <Checkbox
                size="xs"
                checked={
                  documents.length > 0 &&
                  documents.length <= MAX_DOCUMENTS &&
                  documents.every((doc) =>
                    selectedDocuments.has(doc.documentId)
                  )
                }
                indeterminate={
                  documents.length > 0 &&
                  documents.some((doc) =>
                    selectedDocuments.has(doc.documentId)
                  ) &&
                  !documents.every((doc) =>
                    selectedDocuments.has(doc.documentId)
                  )
                }
                onChange={(e) => onSelectAll?.(e.currentTarget.checked)}
                disabled={documents.length > MAX_DOCUMENTS}
              />
            ),
            width: 4,
            render: (document) => (
              <div
                key={`checkbox-${document.documentId}`}
                onClick={(e) => e.stopPropagation()}
              >
                <Checkbox
                  size="xs"
                  checked={selectedDocuments.has(document.documentId)}
                  onChange={(e) =>
                    onSelectDocument?.(
                      document.documentId,
                      e.currentTarget.checked
                    )
                  }
                  disabled={
                    !selectedDocuments.has(document.documentId) &&
                    selectedDocuments.size >= MAX_DOCUMENTS
                  }
                />
              </div>
            ),
          },
        ]
      : []),
    // ID column
    {
      key: 'documentId',
      label: 'ID',
      width: 4,
      render: (document) => (
        <div
          key={`id-${document.documentId}`}
          onClick={(e) => e.stopPropagation()}
        >
          <Tooltip label={document.documentId} position="top" withArrow>
            <Copy
              size={14}
              className="cursor-pointer stroke-doubleNickel-gray-600 hover:stroke-doubleNickel-gray-900"
              onClick={() => {
                navigator.clipboard.writeText(document.documentId)
                toast('Document ID copied to clipboard!', 'success')
              }}
            />
          </Tooltip>
        </div>
      ),
    },
    // Category column
    {
      key: 'category',
      label: 'Category',
      width: 14,
      render: (document) => (
        <div
          key={`category-${document.documentId}`}
          onClick={(e) => e.stopPropagation()}
        >
          <StyledMenu>
            <Menu.Target>
              <div className="flex flex-row items-center justify-between">
                <span className="truncate text-xs">
                  {formatDocumentCategory(document.category)}
                </span>
                <div className="flex items-center gap-1">
                  {showAiIndicators && (
                    <>
                      <AiCategoryIndicator
                        documentIntelligence={document.documentIntelligence}
                        category={document.category}
                      />
                      {/* Generic "AI analyzed" fallback for rows where neither
                          per-field sparkle renders — e.g. MVR docs (grouped
                          findings, no scalar prediction) or a completed analysis
                          whose only prediction has no usable value. Suppressed
                          when a category or expiration sparkle already shows, so
                          a row never carries two sparkles. (AiAnalyzedIndicator
                          additionally self-suppresses when every reviewable
                          suggestion was rejected and there are no findings.) */}
                      {!willFieldSparkleRender(
                        'CATEGORY',
                        document.documentIntelligence
                      ) &&
                        !willFieldSparkleRender(
                          'DATE',
                          document.documentIntelligence
                        ) && (
                          <AiAnalyzedIndicator
                            documentIntelligence={document.documentIntelligence}
                          />
                        )}
                    </>
                  )}
                  <ActionIcon variant="subtle" size="xs">
                    <IconChevronDown className="h-3 fill-none stroke-doubleNickel-gray-600" />
                  </ActionIcon>
                </div>
              </div>
            </Menu.Target>
            <Menu.Dropdown className="h-80 w-40 overflow-auto">
              {categories.map((cat, index) => (
                <Menu.Item
                  key={index}
                  className="text-doubleNickel-gray-700"
                  onClick={() => {
                    if (cat.value !== document.category) {
                      updateDocument({
                        variables: {
                          id: document.documentId,
                          input: {
                            category: cat.value,
                          },
                        },
                      })
                    }
                  }}
                >
                  {cat.label}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </StyledMenu>
        </div>
      ),
    },
    // File Name column
    {
      key: 'fileName',
      label: 'File Name',
      width: 32,
      render: (document) => (
        <div
          key={`fileName-${document.documentId}`}
          className="max-w-xs"
          onClick={(e) => e.stopPropagation()}
        >
          <Tooltip label={document.fileName} position="top" withArrow>
            <Anchor
              className="block truncate text-xs text-doubleNickel-gray-900 hover:text-doubleNickel-black"
              onClick={() => onPreview(document)}
            >
              {document.fileName}
            </Anchor>
          </Tooltip>
        </div>
      ),
    },
    // Status column
    {
      key: 'status',
      label: 'Status',
      width: 18,
      render: (document) => (
        <div
          key={`status-${document.documentId}`}
          onClick={(e) => e.stopPropagation()}
        >
          <StyledMenu>
            <Menu.Target>
              <div>
                <StyledBadge
                  clickable
                  textTransform="capitalize"
                  color={colorByStatus[document.userStatus]}
                  rightSection={<IconChevronDown className="h-3" />}
                >
                  {formatSnakeValue(document.userStatus)}
                </StyledBadge>
              </div>
            </Menu.Target>
            <Menu.Dropdown className="w-40">
              {statuses.map((status, index) => (
                <Menu.Item
                  key={index}
                  className="text-doubleNickel-gray-700"
                  onClick={() => handleStatusChange(document, status)}
                >
                  {formatSnakeValue(status)}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </StyledMenu>
        </div>
      ),
    },
    // Uploaded By column
    {
      key: 'uploadedBy',
      label: 'Uploaded',
      width: 10,
      render: (document) => (
        <div key={`uploadedBy-${document.documentId}`}>
          {document?.uploadedBy ? (
            <Tooltip
              label={buildFullName(document.uploadedBy)}
              position="top"
              withArrow
            >
              <Avatar
                classNames={{
                  placeholder:
                    'bg-doubleNickel-brand-25 text-doubleNickel-brand-500',
                }}
                size="sm"
                radius="sm"
              >
                {document.uploadedBy.firstName?.[0] || ''}
                {document.uploadedBy.lastName?.[0] || ''}
              </Avatar>
            </Tooltip>
          ) : (
            <span className="text-xs text-doubleNickel-gray-400">-</span>
          )}
        </div>
      ),
    },
    // Created At column
    {
      key: 'createdAt',
      label: 'Created At',
      width: 10,
      render: (document) => (
        <div key={`createdAt-${document.documentId}`}>
          <Tooltip
            label={dayjs(document.createdAt).format('MM/DD/YYYY HH:mm:ss')}
            position="top"
            withArrow
          >
            <span className="cursor-default text-xs text-doubleNickel-gray-900">
              {dayjs(document.createdAt).format('MM/DD/YYYY')}
            </span>
          </Tooltip>
        </div>
      ),
    },
    // Expiration Date column
    {
      key: 'expirationDate',
      label: 'Expiration',
      width: 12,
      render: (document) => {
        const isTracked = isCategoryTracked(
          document.category as DocumentCategory
        )
        return (
          <div className="flex flex-row items-center gap-1.5">
            <ExpirationIndicator
              expirationDate={document.expirationDate}
              isRenewed={document.isRenewed}
              isTrackedCategory={isTracked}
            />
            {showAiIndicators && (
              <AiExpirationIndicator
                documentIntelligence={document.documentIntelligence}
                expirationDate={document.expirationDate}
              />
            )}
          </div>
        )
      },
    },
    // Tracker column
    {
      key: 'tracker',
      label: '',
      width: 4,
      render: (document) => {
        const trackerId = getActiveTrackerId(document)
        if (!trackerId) return <div />
        return (
          <CompanyFeatureGate feature="expirationTrackingEnabled">
            <div
              key={`tracker-${document.documentId}`}
              onClick={(e) => e.stopPropagation()}
            >
              <TrackerIconButton
                trackerId={trackerId}
                onClick={onOpenTracker}
              />
            </div>
          </CompanyFeatureGate>
        )
      },
    },
    // Actions column
    {
      key: 'actions',
      label: '',
      width: 6,
      render: (document) => (
        <div
          key={`actions-${document.documentId}`}
          className="flex justify-end"
          onClick={(e) => e.stopPropagation()}
        >
          <Menu shadow="md" position="bottom-end">
            <Menu.Target>
              <ActionIcon
                variant="transparent"
                className="h-3.5 w-3.5 hover:bg-transparent"
              >
                <IconThreeDots className="fill-doubleNickel-white stroke-doubleNickel-gray-600" />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown className="w-40">
              <Menu.Item
                className="text-doubleNickel-gray-700"
                onClick={() => onPreview(document)}
              >
                View
              </Menu.Item>
              <Menu.Item
                className="text-doubleNickel-gray-700"
                onClick={() => onEdit(document, null)}
              >
                Edit
              </Menu.Item>
              <Menu.Item
                className="text-doubleNickel-gray-700"
                onClick={() => onDownload(document.documentId)}
              >
                Download
              </Menu.Item>
              {canCreateTrackerFor(document) && (
                <Menu.Item
                  className="text-doubleNickel-gray-700"
                  onClick={() =>
                    createTrackerForDocument({
                      variables: { documentId: document.documentId },
                    })
                  }
                >
                  Track expiration
                </Menu.Item>
              )}
              <Menu.Item
                className="text-doubleNickel-gray-700"
                onClick={() =>
                  onRenewed(document.documentId, !document.isRenewed)
                }
              >
                Mark as {document.isRenewed ? 'not renewed' : 'renewed'}
              </Menu.Item>
              <Menu.Item
                disabled={Boolean(document.voe)}
                className="text-doubleNickel-gray-700"
                onClick={() => onDelete(document.documentId, null)}
              >
                Delete
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </div>
      ),
    },
  ]

  return (
    <>
      <DnTable
        columns={columns}
        data={documents}
        loading={loading}
        enablePagination={false}
        emptyStateMessage="Upload a new document to get started"
        emptyStateIcon={<IconEmptyInbox className="h-40 fill-none" />}
      />
      {voeCompletionModal}
    </>
  )
}

export default DocumentsTable
